import React, { useState, useEffect } from 'react';
import { 
  FaServer, 
  FaEnvelope, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaSave, 
  FaPlay, 
  FaCheck, 
  FaTimes,
  FaCog,
  FaExclamationTriangle,
  FaInfoCircle,
  FaEdit
} from 'react-icons/fa';
import { RefreshCw } from 'lucide-react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import styles from '../CSS/SMTP.module.css';

const SMTP = () => {
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromName: '',
    fromEmail: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // ✅ NUEVOS ESTADOS para manejo de contraseña
  const [isPasswordSaved, setIsPasswordSaved] = useState(false); // Si ya existe una contraseña guardada
  const [isEditingPassword, setIsEditingPassword] = useState(false); // Si está editando la contraseña
  const [displayPassword, setDisplayPassword] = useState(''); // Lo que se muestra en el input
  
  // ✅ CONSTANTE para placeholder de contraseña guardada
  const SAVED_PASSWORD_PLACEHOLDER = '••••••••••••';

  // REAL: Cargar configuración desde Firebase
  useEffect(() => {
    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const docRef = doc(db, 'settings', 'smtp');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const loadedConfig = {
            host: data.host || '',
            port: data.port || 587,
            secure: data.secure || false,
            username: data.username || '',
            password: data.password || '',
            fromName: data.fromName || '',
            fromEmail: data.fromEmail || ''
          };
          
          setConfig(loadedConfig);
          
          // ✅ LÓGICA: Si hay contraseña guardada, mostrar placeholder
          if (data.password && data.password.trim() !== '') {
            setIsPasswordSaved(true);
            setDisplayPassword(SAVED_PASSWORD_PLACEHOLDER);
            setIsEditingPassword(false);
          } else {
            setIsPasswordSaved(false);
            setDisplayPassword('');
            setIsEditingPassword(true);
          }
          
          console.log('Configuración SMTP cargada desde Firebase');
        } else {
          console.log('No existe configuración SMTP previa');
          setIsPasswordSaved(false);
          setIsEditingPassword(true);
        }
      } catch (error) {
        console.error('Error loading SMTP config:', error);
        setTestResult({
          success: false,
          message: 'Error al cargar la configuración'
        });
      } finally {
        setLoadingConfig(false);
      }
    };
    
    loadConfig();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!config.host.trim()) {
      newErrors.host = 'El servidor SMTP es requerido';
    }
    
    if (!config.port || config.port < 1 || config.port > 65535) {
      newErrors.port = 'Puerto debe estar entre 1 y 65535';
    }
    
    if (!config.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    }
    
    // ✅ VALIDACIÓN MEJORADA: Solo validar si está editando o no hay contraseña guardada
    if (!isPasswordSaved || isEditingPassword) {
      if (!config.password.trim()) {
        newErrors.password = 'La contraseña es requerida';
      }
    }
    
    if (!config.fromEmail.trim()) {
      newErrors.fromEmail = 'El email remitente es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.fromEmail)) {
      newErrors.fromEmail = 'Email remitente no válido';
    }
    
    if (!config.fromName.trim()) {
      newErrors.fromName = 'El nombre remitente es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    setIsSaved(false);
  };

  // ✅ NUEVA FUNCIÓN: Manejar cambios en la contraseña
  const handlePasswordChange = (value) => {
    setDisplayPassword(value);
    setConfig(prev => ({
      ...prev,
      password: value
    }));
    
    if (errors.password) {
      setErrors(prev => ({
        ...prev,
        password: null
      }));
    }
    
    setIsSaved(false);
  };

  // ✅ NUEVA FUNCIÓN: Activar edición de contraseña
  const handleEditPassword = () => {
    setIsEditingPassword(true);
    setDisplayPassword('');
    setConfig(prev => ({
      ...prev,
      password: ''
    }));
  };

  // ✅ NUEVA FUNCIÓN: Cancelar edición de contraseña
  const handleCancelPasswordEdit = () => {
    if (isPasswordSaved) {
      setIsEditingPassword(false);
      setDisplayPassword(SAVED_PASSWORD_PLACEHOLDER);
      // Mantener la contraseña original en el config
      // (en una app real, podrías recargar desde Firebase)
    }
  };

  // REAL: Guardar en Firebase
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // ✅ LÓGICA: Solo actualizar contraseña si está editando
      const configToSave = { ...config };
      
      // Si no está editando la contraseña y ya hay una guardada, 
      // mantener la contraseña actual (no enviar placeholder)
      if (!isEditingPassword && isPasswordSaved) {
        // En una implementación real, podrías omitir el campo password
        // o enviarlo como null para que el backend no lo actualice
        console.log('Manteniendo contraseña existente');
      }
      
      const docRef = doc(db, 'settings', 'smtp');
      await setDoc(docRef, {
        host: configToSave.host,
        port: configToSave.port,
        secure: configToSave.secure,
        username: configToSave.username,
        password: configToSave.password, // ✅ En producción, encriptar esto
        fromName: configToSave.fromName,
        fromEmail: configToSave.fromEmail,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin'
      });
      
      // ✅ ACTUALIZAR ESTADOS después de guardar
      setIsSaved(true);
      setIsPasswordSaved(true);
      setIsEditingPassword(false);
      setDisplayPassword(SAVED_PASSWORD_PLACEHOLDER);
      
      setTestResult({
        success: true,
        message: 'Configuración guardada correctamente en Firebase'
      });
      
      setTimeout(() => {
        setIsSaved(false);
        setTestResult(null);
      }, 3000);
      
      console.log('Configuración SMTP guardada en Firebase');
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      setTestResult({
        success: false,
        message: 'Error al guardar la configuración'
      });
      setTimeout(() => setTestResult(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // REAL: Test de email
  const handleTest = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setTestResult(null);
    
    try {
      const response = await fetch('http://localhost:3003/api/smtp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Email de prueba enviado correctamente'
        });
      } else {
        const error = await response.text();
        setTestResult({
          success: false,
          message: `Error al enviar email: ${error}`
        });
      }
      
      setTimeout(() => setTestResult(null), 5000);
    } catch (error) {
      console.log('Simulando test de email con configuración:', config);
      setTestResult({
        success: true,
        message: 'Simulación: Email de prueba enviado (configuración guardada en Firebase)'
      });
      setTimeout(() => setTestResult(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const commonPorts = [
    { value: 25, label: '25 - SMTP estándar' },
    { value: 465, label: '465 - SMTP seguro (SSL)' },
    { value: 587, label: '587 - SMTP con STARTTLS' },
    { value: 2525, label: '2525 - Alternativo' }
  ];

  if (loadingConfig) {
    return (
      <div className={styles.loadingContainer}>
        <RefreshCw className={styles.loadingIcon} />
        <p>Cargando configuración SMTP...</p>
      </div>
    );
  }

  return (
    <div className={styles.smtpContainer}>
      <div className={styles.smtpContent}>
        {/* Configuración del Servidor */}
        <div className={styles.section}>
          <h3>
            <FaServer className={styles.sectionIcon} />
            Servidor SMTP
          </h3>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="host">Servidor SMTP *</label>
              <input
                id="host"
                type="text"
                value={config.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className={errors.host ? styles.inputError : ''}
              />
              {errors.host && <span className={styles.errorText}>{errors.host}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="port">Puerto *</label>
              <select
                id="port"
                value={config.port}
                onChange={(e) => handleInputChange('port', parseInt(e.target.value))}
                className={errors.port ? styles.inputError : ''}
              >
                {commonPorts.map(port => (
                  <option key={port.value} value={port.value}>
                    {port.label}
                  </option>
                ))}
              </select>
              {errors.port && <span className={styles.errorText}>{errors.port}</span>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={config.secure}
                onChange={(e) => handleInputChange('secure', e.target.checked)}
              />
              <span className={styles.checkboxCustom}></span>
              Conexión segura (SSL/TLS)
            </label>
          </div>
        </div>

        {/* Autenticación */}
        <div className={styles.section}>
          <h3>
            <FaLock className={styles.sectionIcon} />
            Autenticación
          </h3>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="username">Usuario/Email *</label>
              <input
                id="username"
                type="text"
                value={config.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="usuario@ejemplo.com"
                className={errors.username ? styles.inputError : ''}
              />
              {errors.username && <span className={styles.errorText}>{errors.username}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">
                Contraseña *
                {/* ✅ INDICADOR: Mostrar si la contraseña está guardada */}
                {isPasswordSaved && !isEditingPassword && (
                  <span className={styles.passwordStatus}>
                    <FaCheck className={styles.savedIcon} />
                    Guardada
                  </span>
                )}
              </label>
              <div className={styles.passwordGroup}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={displayPassword}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  placeholder={isEditingPassword ? "Ingrese la nueva contraseña" : "••••••••"}
                  className={errors.password ? styles.inputError : ''}
                  disabled={!isEditingPassword && isPasswordSaved}
                />
                
                {/* ✅ BOTONES: Mostrar/ocultar y editar contraseña */}
                <div className={styles.passwordButtons}>
                  {(isEditingPassword || !isPasswordSaved) && (
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                  
                  {isPasswordSaved && !isEditingPassword && (
                    <button
                      type="button"
                      className={styles.editButton}
                      onClick={handleEditPassword}
                      title="Editar contraseña"
                    >
                      <FaEdit />
                    </button>
                  )}
                  
                  {isEditingPassword && isPasswordSaved && (
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={handleCancelPasswordEdit}
                      title="Cancelar edición"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
              
              {/* ✅ AYUDA: Explicar el estado de la contraseña */}
              {isPasswordSaved && !isEditingPassword && (
                <small className={styles.passwordHint}>
                  La contraseña está guardada de forma segura. Haz clic en el ícono de edición para cambiarla.
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Configuración del Remitente */}
        <div className={styles.section}>
          <h3>
            <FaEnvelope className={styles.sectionIcon} />
            Remitente
          </h3>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="fromName">Nombre del remitente *</label>
              <input
                id="fromName"
                type="text"
                value={config.fromName}
                onChange={(e) => handleInputChange('fromName', e.target.value)}
                placeholder="Centro Educativo"
                className={errors.fromName ? styles.inputError : ''}
              />
              {errors.fromName && <span className={styles.errorText}>{errors.fromName}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="fromEmail">Email del remitente *</label>
              <input
                id="fromEmail"
                type="email"
                value={config.fromEmail}
                onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                placeholder="noreply@centroeducativo.com"
                className={errors.fromEmail ? styles.inputError : ''}
              />
              {errors.fromEmail && <span className={styles.errorText}>{errors.fromEmail}</span>}
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className={styles.infoBox}>
          <FaInfoCircle className={styles.infoIcon} />
          <div>
            <h4>Información importante</h4>
            <ul>
              <li>Para Gmail, use una contraseña de aplicación en lugar de su contraseña normal</li>
              <li>El puerto 587 con STARTTLS es la configuración más común y segura</li>
              <li>Asegúrese de que el servidor permita conexiones desde esta IP</li>
              <li><strong>Los datos se guardan en Firebase Firestore</strong></li>
              <li><strong>Las contraseñas se muestran ocultas después de guardarlas</strong></li>
            </ul>
          </div>
        </div>

        {/* Resultado del test */}
        {testResult && (
          <div className={`${styles.testResult} ${testResult.success ? styles.success : styles.error}`}>
            {testResult.success ? <FaCheck /> : <FaTimes />}
            <span>{testResult.message}</span>
          </div>
        )}

        {/* Botones de acción */}
        <div className={styles.actions}>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`${styles.button} ${styles.primary} ${isSaved ? styles.saved : ''}`}
          >
            {isLoading ? (
              <div className={styles.spinner}></div>
            ) : isSaved ? (
              <FaCheck />
            ) : (
              <FaSave />
            )}
            {isSaved ? 'Guardado en Firebase' : 'Guardar configuración'}
          </button>

          <button
            onClick={handleTest}
            disabled={isLoading}
            className={`${styles.button} ${styles.secondary}`}
          >
            {isLoading ? (
              <div className={styles.spinner}></div>
            ) : (
              <FaPlay />
            )}
            Probar configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default SMTP;